import { assertValue } from '@shared/helpers'
import { ENDPOINT_SIGN_IFP_APPLICATION } from '@shared/sec-loans'
import { ENDPOINT_LOG_ERROR, SignApplicationResponse } from '@shared/types'
import { StorageKey } from '@ui-common/types'
import { callEndpoint } from '@ui-common/utils'
import {
  AuthCallbackResult,
  AuthErrorCode,
  cleanUpAuthQueryParams,
  isCancelledCallbackResult,
  parseAuthCode,
} from '@ui-common/utils/authentication'
import { debugLog, timestamp } from '@ui-common/utils/helpers'
import { removeSessionStorageItem, setSessionStorageItem } from '@ui-common/utils/storage'
import { navigateToExternalUrl } from '@ui-components/utilities/navigation'
import { assign, ErrorActorEvent, fromPromise, setup, StateFrom } from 'xstate'

interface AuthError {
  authError: AuthErrorCode
  url: string
}

export interface SignStepContext {
  signingUrl?: string
  authCode?: string
  allowRetry?: boolean
  error?: AuthError
  applicationId?: string
}

interface SignStepInput {
  applicationId: string
}

type SignStepEvent = { type: 'PARSE_AUTH_CODE' } | { type: 'SIGN' }

const GET_SIGNING_URL = 'getSigningUrl'
const SIGNING_ERROR = 'signingError'

export const GET_AUTH_CODE = 'getAuthCode'
export const PARSE_AUTH_CODE = 'parseAuthCode'
export const END_STEP = 'endStep'
export const signStepMachineId = 'signStepMachine'

type SignStepMachineType = StateFrom<ReturnType<typeof signStepMachine>>

export const shouldHydrateState = (state: SignStepMachineType): boolean => {
  return state?.value === GET_AUTH_CODE
}

const logSigningErrorAction = {
  type: 'logSigningError',
  params: ({ event }: { event: ErrorActorEvent }) => event.error,
} as const

export const signStepMachine = (actions: {
  onDone: (authCode: string) => void
  onCancel: () => void
  onError: () => void
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
}) =>
  setup({
    types: {
      context: {} as SignStepContext,
      events: {} as SignStepEvent,
      input: {} as SignStepInput,
    },
    actors: {
      parseAuthCode: fromPromise((): Promise<AuthCallbackResult> => {
        debugLog('Service: parseAuthCode')
        return parseAuthCode()
      }),
      getSigningUrl: fromPromise<SignApplicationResponse, string>(({ input }): Promise<SignApplicationResponse> => {
        return callEndpoint(ENDPOINT_SIGN_IFP_APPLICATION, { pathParams: { applicationId: input } })
      }),
    },
    actions: {
      signingCancelled: () => actions.onCancel(),
      signingSuccess: ({ context }) => {
        actions.onDone(assertValue(context.authCode, 'context.authCode'))
      },
      signingErrored: () => {
        actions.onError()
      },
      storeAuthCode: assign((_, authCode?: string) => {
        assertValue(authCode, 'authCode')
        debugLog('Action: storeAuthCode')
        return {
          authCode,
        }
      }),
      storeError: assign((_, error: unknown) => {
        debugLog('Action: storeError', error)
        return {
          error: isAuthError(error) ? error : undefined,
        }
      }),
      cleanUpSignStorage: () => {
        removeSessionStorageItem(StorageKey.SignStarted)
      },
      cleanupAuthParam: () => {
        cleanUpAuthQueryParams()
      },
      redirectToSigning: (_, authUrl: string) => {
        debugLog('Action: redirectToSigning')
        setSessionStorageItem(StorageKey.SignStarted, timestamp())
        debugLog('Action: redirectToSigning - redirecting...')
        navigateToExternalUrl(authUrl)
      },
      setSigningUrl: assign((_, signingUrl: string) => ({ signingUrl })),
      logSigningError: (_, error: unknown) => {
        debugLog('Signing error', error)
        void callEndpoint(ENDPOINT_LOG_ERROR, { requestPayload: { error: JSON.stringify(error) } })
      },
    },
    guards: {
      navigatedBack: (_, data: AuthCallbackResult) => {
        const result = !!data.isBackNavigation
        debugLog(`Guard: navigatedBack: ${result}`)
        return result
      },
      authCodeWasFound: (_, authCode?: string) => {
        const result = !!authCode
        debugLog(`Guard: authCodeWasFound: ${result}`)
        return result
      },
      isCancelledByUser: (_, data: AuthCallbackResult): boolean => isCancelledCallbackResult(data),
    },
  }).createMachine({
    id: signStepMachineId,
    initial: GET_SIGNING_URL,
    context: ({ input }) => ({ applicationId: input.applicationId }),
    states: {
      [GET_SIGNING_URL]: {
        invoke: {
          src: 'getSigningUrl',
          input: ({ context }) => assertValue(context.applicationId, 'context.applicationId'),
          onDone: {
            target: GET_AUTH_CODE,
            actions: { type: 'setSigningUrl', params: ({ event }) => event.output.signingUrl },
          },
          onError: { target: SIGNING_ERROR, actions: ['signingErrored'] },
        },
      },
      [GET_AUTH_CODE]: {
        entry: {
          type: 'redirectToSigning',
          params: ({ context }) => assertValue(context.signingUrl, 'context.signingUrl'),
        },
        on: {
          PARSE_AUTH_CODE,
        },
      },
      [PARSE_AUTH_CODE]: {
        invoke: {
          src: 'parseAuthCode',
          input: ({ context }) => context,
          onDone: [
            {
              guard: { type: 'authCodeWasFound', params: ({ event }) => event.output?.authCode },
              actions: [
                { type: 'storeAuthCode', params: ({ event }) => event.output?.authCode },
                'cleanupAuthParam',
                'cleanUpSignStorage',
                'signingSuccess',
              ],
              target: END_STEP,
            },
            {
              target: SIGNING_ERROR,
              actions: [{ type: 'logSigningError', params: () => 'Auth code was not found' }, 'signingErrored'],
            },
          ],
          onError: [
            {
              target: SIGNING_ERROR,
              guard: { type: 'navigatedBack', params: ({ event }) => event.error as AuthCallbackResult },
              actions: [logSigningErrorAction, 'cleanUpSignStorage', 'signingCancelled'],
            },
            {
              target: SIGNING_ERROR,
              guard: { type: 'isCancelledByUser', params: ({ event }) => event.error as AuthCallbackResult },
              actions: [logSigningErrorAction, 'cleanupAuthParam', 'cleanUpSignStorage', 'signingCancelled'],
            },
            {
              target: SIGNING_ERROR,
              actions: [
                logSigningErrorAction,
                {
                  type: 'storeError',
                  params: ({ event }: { event: ErrorActorEvent }) => event.error,
                },
                'cleanupAuthParam',
                'cleanUpSignStorage',
                'signingErrored',
              ],
            },
          ],
        },
      },
      [SIGNING_ERROR]: {
        on: {
          SIGN: { target: GET_AUTH_CODE },
        },
      },
      [END_STEP]: {
        type: 'final',
      },
    },
  })

const isAuthError = (error: unknown): error is AuthError => {
  return (error as AuthError).authError !== undefined
}
